import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Tag, ArrowRight, BookOpen } from 'lucide-react';
import Seo from '@/components/shared/Seo';
import Breadcrumbs from '@/components/shared/Breadcrumbs';
import { articles, listCategories } from '@/data/blog/articles';
import { combineSchemas, organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema } from '@/lib/schemaOrg';

const BlogIndexPage = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const categories = ['All', ...listCategories()];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return articles.filter((a) => {
      if (cat !== 'All' && a.category !== cat) return false;
      if (!needle) return true;
      return (
        a.title.toLowerCase().includes(needle) ||
        a.description.toLowerCase().includes(needle) ||
        (a.keywords || []).some((k) => k.toLowerCase().includes(needle))
      );
    });
  }, [q, cat]);

  const schemas = combineSchemas(
    organizationSchema(),
    websiteSchema(),
    webPageSchema({
      title: 'Winhouse Knowledge Hub — Global tutoring, IELTS, VCE, admissions',
      description: 'Free guides on IELTS, PTE, TOEFL, SAT, GMAT, GRE, VCE, Selective Entry and international university admissions.',
      path: '/blog',
    }),
    breadcrumbSchema([{ name: 'Knowledge Hub', path: '/blog' }])
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="blog-index">
      <Seo
        title="Winhouse Knowledge Hub | IELTS, VCE, Study Abroad Guides"
        description="Free guides on IELTS, PTE, TOEFL, SAT, GMAT, GRE, VCE, Selective Entry and international university admissions — from the Winhouse editorial team."
        path="/blog"
        jsonLd={schemas}
      />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs items={[{ name: 'Knowledge Hub', path: '/blog' }]} />

        <div className="mt-6 mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-bold">Winhouse Knowledge Hub</div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black text-black leading-tight" data-testid="blog-h1">
            Practical guides for tutoring, tests and admissions
          </h1>
          <p className="mt-3 text-lg text-gray-700 max-w-3xl">In-depth, editor-reviewed writing on IELTS, PTE, TOEFL, SAT, GMAT, GRE, VCE, Selective Entry and international university admissions.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="blog-controls">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-3.5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles"
              className="w-full h-11 pl-10 pr-4 rounded-full border border-gray-200 text-sm outline-none focus:border-red-600"
              data-testid="blog-search" />
          </div>
          <div className="flex gap-1 overflow-x-auto pb-1">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`h-11 px-4 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${cat === c ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'}`}
                data-testid={`blog-cat-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="blog-list">
          {filtered.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`}
              className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-red-300 transition"
              data-testid={`blog-card-${a.slug}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-red-700 font-bold">
                <Tag className="h-3 w-3" /> {a.category}
                <span className="text-gray-400 font-normal normal-case flex items-center gap-1 ml-2"><Clock className="h-3 w-3" /> {a.readingMinutes} min</span>
              </div>
              <h2 className="mt-2 text-lg font-bold text-black group-hover:text-red-700 transition">{a.title}</h2>
              <p className="mt-1.5 text-sm text-gray-600 leading-relaxed line-clamp-3">{a.description}</p>
              <div className="mt-3 text-xs text-red-700 font-bold inline-flex items-center gap-1">
                Read article <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-sm text-gray-500">
              <BookOpen className="h-6 w-6 mx-auto mb-2 text-gray-300" /> Nothing matches — try a different search.
            </div>
          )}
        </div>

        <div className="mt-12 bg-black text-white rounded-2xl p-6 sm:p-8" data-testid="blog-bottom-cta">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D3D86F] font-bold">Ready to work with a tutor?</div>
          <h3 className="mt-2 text-2xl font-black">Turn a guide into a plan</h3>
          <p className="mt-2 text-white/70 text-sm max-w-2xl">Reading an article is a start. Book a trial lesson and get a personalised plan for your target test or admissions goal.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/get-started" className="h-11 px-6 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5">Get Started</Link>
            <Link to="/tutors" className="h-11 px-6 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5">Find a Tutor</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogIndexPage;
